import { useRef } from 'react';

import { CHART_HEIGHT } from '#/components/charts/TimeseriesChart';
import { DetailSectionNav } from '#/components/detail/DetailSectionNav';
import { DETAIL_SECTION_NAV } from '#/components/detail/sectionNav';
import {
  groupClass,
  rowClass,
  valueClass,
} from '#/components/detail/StatGroup';
import { ContentLayout } from '#/components/layout/ContentLayout';
import {
  Skeleton,
  SkeletonRegion,
  SkeletonText,
} from '#/components/ui/Skeleton';
import type { ProductTypeSlug } from '#/types';
import { cn } from '#/utils/cn';

function SectionHeaderSkeleton() {
  return (
    <div className="flow flow-inner-8">
      <Skeleton className="h-8 w-28" />
      <Skeleton className="text-fluid-subheader h-rel-1 w-1/3" />
      <SkeletonText lines={3} />
    </div>
  );
}

function StatGroupSkeleton({ id, rows }: { id: string; rows: number }) {
  return (
    <div id={id} className={groupClass}>
      <Skeleton className="text-fluid-subheader h-rel-1 w-128 border-0" />
      <SkeletonText lines={2} className="mb-12 border-0 pt-8" />
      {Array.from({ length: rows }, (_, row) => (
        <div key={row} className={rowClass}>
          <div className="flow flow-inner-8 w-full">
            <Skeleton className="h-rel-1 w-144" />
            <SkeletonText lines={3} className="text-static-12" />
          </div>
          <Skeleton className={cn('h-14', valueClass)} />
        </div>
      ))}
    </div>
  );
}

function ChartSkeleton() {
  return (
    <div className="p-gap-inset flow flow-inner-20 px-16">
      <Skeleton className="w-full" style={{ height: CHART_HEIGHT }} />
      <Skeleton className="h-144 w-1/2" />
    </div>
  );
}

// Exported so a section behind its own query boundary can render it as that
// boundary's loading state, holding its id in the DOM for the section nav.
export function TableSectionSkeleton({
  id,
  rows = 6,
}: {
  id: string;
  rows?: number;
}) {
  return (
    <section id={id} className="flow flow-inner-8 scroll-mt-(--scroll-offset)">
      <SectionHeaderSkeleton />
      <div className="flow-32 p-gap-inset border-divider-subtle -mx-gap text-static-14 border-t border-b sm:mx-0 sm:rounded sm:border">
        <div className="divide-divider-subtle divide-y">
          {Array.from({ length: rows }, (_, row) => (
            <div key={row} className="flex items-center gap-16 py-16">
              <Skeleton className="h-rel-1 flex-1" />
              <Skeleton className="h-rel-1 w-1/6" />
              <Skeleton className="h-rel-1 w-1/6" />
              <Skeleton className="h-rel-1 hidden w-1/6 sm:block" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

interface SummarySkeletonProps {
  category: ProductTypeSlug;
}

function SummarySkeleton({ category }: SummarySkeletonProps) {
  const COMPANY_COLUMNS = category === 'equities' ? 4 : 3;
  return (
    <div id="summary" className="flow flow-22">
      {/* Ticker, Title, Updated */}
      <Skeleton className="mb-1 h-28 w-96" />
      <Skeleton className="text-fluid-header flow-12 h-rel-1 w-1/2" />
      <Skeleton className="flow-12 h-22 w-160" />

      <div className="flex flex-col items-start justify-center gap-16 md:flex-row md:items-center md:justify-between">
        <Skeleton className="h-48 w-full rounded md:w-1/3" />
        <Skeleton className="h-20 w-144" />
      </div>

      <div className="flow flow-inner-20">
        <SkeletonText lines={6} />
        <Skeleton className="h-16 w-180" />
      </div>

      <div
        className={cn(
          'border-blue-30 grid grid-cols-2 gap-x-16 gap-y-22 border-t pt-22',
          COMPANY_COLUMNS === 4 ? 'md:grid-cols-4' : 'md:grid-cols-3',
        )}
      >
        {Array.from({ length: COMPANY_COLUMNS }, (_, cell) => (
          <div key={cell} className="flex flex-col gap-8">
            <Skeleton className="h-16 w-2/3" />
            <Skeleton className="h-16 w-1/2" />
            {cell === 3 && <Skeleton className="h-16 w-full" />}
          </div>
        ))}
      </div>
    </div>
  );
}

function PerformanceSkeleton({
  statGroupRows: [keyStats, price, risk],
}: {
  statGroupRows: [number, number, number];
}) {
  return (
    <section id="performance" className="flow flow-32">
      <SectionHeaderSkeleton />

      <div className="-mx-gap sm:border-divider-subtle divide-divider-subtle divide-y sm:mx-0 sm:rounded sm:border">
        <ChartSkeleton />
        <StatGroupSkeleton id="key-stats" rows={keyStats} />
        <StatGroupSkeleton id="price" rows={price} />
        <StatGroupSkeleton id="risk" rows={risk} />
      </div>
    </section>
  );
}

interface DetailSkeletonProps {
  category: ProductTypeSlug;
  /** Symbol from the route, so the wait is announced with a subject. */
  symbol?: string;
}

/**
 * Loading state for the detail pages, shaped like the page it stands in for.
 *
 * Mounted twice on a cold load — once as the route's `pendingComponent` while
 * the loader warms content, then again through `QueryBoundary`'s `loading`
 * prop while the detail query resolves. It renders the real `ContentLayout`
 * and the real `DetailSectionNav`, and its section blocks carry the real
 * section ids, so the rail, its jump links and its scroll spy work against the
 * skeleton and nothing shifts when data lands.
 */
export function DetailSkeleton({ category, symbol }: DetailSkeletonProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const priceSummaryRef = useRef<HTMLDivElement>(null);

  const isEquity = category === 'equities';
  // Key Stats / Price / Risk row counts, per type.
  const statGroupRows: [number, number, number] = isEquity
    ? [7, 4, 4]
    : [6, 5, 4];

  return (
    <ContentLayout>
      <DetailSectionNav
        sections={DETAIL_SECTION_NAV[category]}
        contentRef={contentRef}
        priceSummaryRef={priceSummaryRef}
      />

      <SkeletonRegion
        ref={contentRef}
        label={symbol ? `Loading ${symbol}…` : 'Loading…'}
        className="col-content"
      >
        <div className="flow flow-60">
          {/* Breadcrumbs */}
          <Skeleton className="h-20 w-1/2" />

          <SummarySkeleton category={category} />
          <PerformanceSkeleton statGroupRows={statGroupRows} />

          {isEquity ? (
            <>
              <TableSectionSkeleton id="filings" rows={12} />
              <TableSectionSkeleton id="top-holders" rows={11} />
            </>
          ) : (
            <>
              <TableSectionSkeleton id="portfolio" rows={6} />
              <TableSectionSkeleton id="top-holdings" rows={11} />
            </>
          )}

          <section id="related" className="flow flow-inner-20">
            <SectionHeaderSkeleton />
            <div className="grid gap-16 md:grid-cols-3">
              {Array.from({ length: 3 }, (_, card) => (
                <Skeleton
                  key={card}
                  className="border-divider-subtle h-128 rounded"
                />
              ))}
            </div>
          </section>

          <section id="learn-more" className="flow flow-inner-20">
            <SectionHeaderSkeleton />
            <div className="grid gap-16 md:grid-cols-3">
              {Array.from({ length: 3 }, (_, card) => (
                <Skeleton
                  key={card}
                  className="border-divider-subtle h-160 rounded"
                />
              ))}
            </div>
          </section>

          <section className="md:gap-gap grid grid-cols-1 gap-24 md:grid-cols-3">
            {Array.from({ length: 3 }, (_, card) => (
              <div key={card} className="flow flow-inner-8">
                <Skeleton className="aspect-square h-40 rounded-full" />
                <SkeletonText lines={4} className="text-static-12" />
              </div>
            ))}
          </section>
        </div>
      </SkeletonRegion>
    </ContentLayout>
  );
}
